import React, { useState } from 'react';
import './ProductCard.scss';
import { GrDown, GrUp } from 'react-icons/gr';
import TextInput from '../TextInput';
import { Product } from '../../types/Product';
import ProductImage from '../ProductImage';
import { Price } from '../../types/Price';
import { Roll, Stock } from '../../types/Stock';
import { ProductType } from '../../types/ProductType';
import _ from 'lodash';
import Button from '../Button';
import DropDown from '../DropDown';
import MultiChoice from '../MultiChoice';
import LWModal from '../Modal';
import { OrderLine } from '../../types/Order';
import { lwRoundNumber, roundNumber } from '../../helpers/functions';
import { useDispatch, useSelector } from 'react-redux';
import { addToBasket } from '../../actions/Order';
import { useHistory } from 'react-router-dom';
import CONSTANTS from '../../helpers/constants';
import TrashSvg from '../../assets/svgs/trash.svg';
import { emailNotifyStock } from '../../actions/Email';
import { getCustomer } from '../../selectors/Customer';

type Props = {
  product?: Product | null;
  price?: Price | null;
  stock?: Stock | null;
  productType?: ProductType | null
};

type Cut = {
  sqm: number;
  width: number;
  length: number;
  price: number;
  toggle: boolean;
  reference: string;
  product_sku: string;
  product_quality: string;
  colour_full_name: string;
};

const ProductCard = (props: Props) => {
  const { product, price, stock, productType } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const customer = useSelector(getCustomer);
  const [toggleCut, setToggleCut] = useState(true);
  const [toggleRoll, setToggleRoll] = useState(false);
  const [toggleRollPopUp, setToggleRollPopUp] = useState(false);
  const [currentLength, setCurrentLength] = useState<number | null>(null);
  const [reference, setReference] = useState('');
  const [rollReference, setRollReference] = useState('');
  const [selectedRoll, setSelectedRoll] = useState<Roll | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([]);
  const [cuts, setCuts] = useState<Cut[]>([]);
  const widths = _.range(1, 4)
  // @ts-ignore
    .map(index => product?.[`WIDTH${index}`] ? ({ label: product?.[`WIDTH${index}`], value: product?.[`WIDTH${index}`],
    }) : null)
    .filter(l => l) || [];
  const [currentWidth, setCurrentWidth] = useState(widths?.[0]?.value);
  const hasCutStock = () => {
    const longestLength = stock?.roll?.find(r => r.roll_flag === 'L' && r.roll_width === currentWidth)?.roll_length || 0;
    return longestLength > 0;
  };

  const rollsStock = stock?.roll?.filter(r => r.roll_flag !== 'L') || [];

  const onAddCutsToOrderSummary = () => {
    const localOrderLines = [...orderLines];
    if (product && cuts.length > 0 && price && productType) {
      cuts.forEach(c => {
        localOrderLines.push({
          product_sku: c.product_sku,
          product_quality: c.product_quality,
          product_type_code: productType.product_type_code,
          product_type_code_name: productType.product_type_desc,
          quantity: 1,
          length: lwRoundNumber(c.length),
          width: c.width,
          roll_id: '',
          pallet_id: '',
          half_roll: '',
          item_price: price.cut_price,
          line_reference: c.reference,
          line_price: c.length * c.width * price.cut_price,
          line_discount: price.cut_price_discount,
          colour_full_name: c.colour_full_name,
        });
      });
      setOrderLines(localOrderLines);
      setCuts([]);
    }
  };

  const onNotify = () => {
    dispatch(emailNotifyStock({
      customerName: customer?.NAME || 'N/A',
      productQuality: product?.quality_full_name || 'N/A',
      productColor: product?.colour_full_name || 'N/A',
      productSKU: product?.product_sku || 'N/A',
      rep_email: customer?.rep_email || 'N/A',
      rep_name: customer?.rep_name || 'N/A',
    }));
  };

  const onAddRollToOrderSummary = () => {
    const localOrderLines = [...orderLines];
    if (product && selectedRoll && price && productType) {
      localOrderLines.push({
        product_sku: product.product_sku,
        quantity: 1,
        pallet_id: '',
        length: selectedRoll.roll_length,
        product_quality: product.quality_full_name,
        product_type_code: product.product_type_code,
        product_type_code_name: productType.product_type_desc,
        width: product.WIDTH1,
        item_price: price.roll_price,
        roll_id: selectedRoll.roll_id,
        half_roll: selectedRoll.roll_flag,
        line_reference: rollReference,
        line_price: selectedRoll.roll_length * product.WIDTH1 * price.roll_price,
        line_discount: price.roll_price_discount,
        colour_full_name: product.colour_full_name,
      });
      setOrderLines(localOrderLines);
      setSelectedRoll(null);
      setRollReference('');
    }
  };

  const onSelectRoll = () => {
    setToggleRollPopUp(false);
  };

  const onAddOrderSummaryToBasket = () => {
    dispatch(addToBasket(orderLines));
    history.push(CONSTANTS.APP_ROUTES.CART);
  };

  const onAddOrderSummaryToBasketAndContinue = () => {
    dispatch(addToBasket(orderLines));
    history.push(CONSTANTS.APP_ROUTES.DASHBOARD);
  };


  const onAddAnotherCut = () => {
    const localCuts = [...cuts];
    if (currentWidth && currentLength && price?.cut_price && product) {
      localCuts.push({
        width: parseFloat(currentWidth),
        length: lwRoundNumber(currentLength),
        price: price?.cut_price,
        sqm: lwRoundNumber(currentWidth * currentLength),
        toggle: false,
        reference: reference,
        product_sku: product.product_sku,
        product_quality: product.quality_full_name,
        colour_full_name: product.colour_full_name,
      });
      setCuts(localCuts);
      setCurrentLength(null);
      setReference('');
    }
  };

  const toggleICut = (index: number) => cuts[index].toggle;

  const setICutWidth = (index: number, newVal: number) => {
    const localCuts = [...cuts];
    localCuts[index].width = lwRoundNumber(newVal);
    setCuts(localCuts);
  };

  const setICutReference = (index: number, newVal: string) => {
    const localCuts = [...cuts];
    localCuts[index].reference = newVal;
    setCuts(localCuts);
  };
  const setICutLength = (index: number, newVal: number) => {
    const localCuts = [...cuts];
    localCuts[index].length = lwRoundNumber(newVal);
    setCuts(localCuts);
  };

  const setToggleICut = (index: number, newVal: boolean) => {
    const localCuts = [...cuts];
    localCuts[index].toggle = newVal;
    setCuts(localCuts);
  };

  const onMinusFlyCut = (index: number) => {
    const localCuts = [...cuts];
    setCuts(localCuts.filter((c, i) => i !== index));
  };

  const onMinus = (index: number) => {
    let localOrderLines = [...orderLines];
    const o = localOrderLines[index];
    if (o.quantity === 1) {
      localOrderLines = localOrderLines.filter((or, i) => i !== index);
    } else {
      if (index >= 0) {
        localOrderLines[index].quantity -= 1;
        localOrderLines[index].line_price = localOrderLines[index].length * (o?.width || 0) * localOrderLines[index].item_price * localOrderLines[index].quantity;
      }
    }
    setOrderLines(localOrderLines);
  };


  const shallShowUnableColorMatch = () => {
    let contains = false;
    let hash = new Map();
    orderLines.forEach(o => {
      if (hash.has(`${o.colour_full_name}-${o.width}`)) {
        contains = true;
      }
      hash.set(`${o.colour_full_name}-${o.width}`, 1);
    });
    return contains;
  };

  const getOrderTotal = () => {
    let total = 0;
    orderLines.forEach(o => total += o.line_price);
    return total;
  };

  let totalLength = 0;
  stock?.roll?.forEach(r => {
    if (r.roll_flag !== 'L' && r.roll_width === currentWidth) {
      totalLength += r.roll_length;
    }
  });

  const currentUsedLength = () => {
    let length = currentLength || 0;
    cuts.forEach(c => {
      if (c.width === currentWidth && c.product_sku === product?.product_sku) {
        length += c.length;
      }
    });
    orderLines.forEach(c => {
      if (c.width === currentWidth && c.product_sku === product?.product_sku) {
        length += c.length;
      }
    });
    return length;
  };

  const totalSqm = lwRoundNumber(currentWidth && currentLength && currentWidth * currentLength || 0);

  return (
            <div className="product-card">
                <div className="product-card__header">
                    <div className="product-card__header__img">
                        <ProductImage imageCode={product?.product_sku} imageAlt={product?.quality_full_name}/>
                        <div className="product-card__header__img__name">
                            <p className="product-card__header__img__name__top">
                                Product Name: {product?.quality_full_name} {product?.colour_full_name}
                            </p>
                            <p className="product-card__header__img__name__bottom">
                                {_.startCase(_.toLower(productType?.product_type_desc))}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="product-card__sub-header">
                    Select your preferred cut or chose from available rolls
                </div>
                {/*<!-- CUTS ADDED -->*/}
                {cuts.map((c, i) => (
                    <div key={i} className="product-card__accordion mt-3">
                        <div className="product-card__accordion__header">
                            <div className="product-card__accordion__header__title-small">
                                Cut {i + 1} - W {c.width}m x L {c.length}m - [{c.colour_full_name}]
                            </div>
                            <div className="cursor-pointer" onClick={() => onMinusFlyCut(i)}>
                                <img src={TrashSvg} alt="trash"/>
                            </div>
                            <div onClick={() => setToggleICut(i, !toggleICut(i))}
                                 className="product-card__accordion__header__toggle">
                                {!toggleICut(i) && <GrDown className="product-card__caret"/>}
                                {toggleICut(i) && <GrUp className="product-card__caret"/>}
                            </div>
                        </div>
                        {toggleICut(i) && <div className="product-card__accordion__body">
                            <div className="row">
                                <div className="col-md-6">
                                    <div>Width (m)</div>
                                    <div className="mt-3">
                                        {/* @ts-ignore */}
                                        <DropDown items={widths} value={c.width}
                                                  onSelect={(item) => setICutWidth(i, item.value)}/>
                                    </div>
                                    <div className="mt-3">
                                        <TextInput disabled bgColor="white" value={`${c.sqm} SQM`}/>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div>Length (m)</div>
                                    <div className="mt-3">
                                        <TextInput type="number" bgColor="white" onChange={(t) => setICutLength(i, t)}
                                                   value={c.length}/>
                                    </div>

                                </div>
                            </div>
                            <div className="row mt-5">
                                <TextInput required label="Reference" placeholder="Example: Bedroom" bgColor="white" value={c.reference} onChange={(t) => setICutReference(i, t)}/>
                            </div>
                            <div className="row mt-5">
                                <div className="col-3">
                                    <span className="font-bold font-size-title">Total</span>
                                </div>
                                <div className="col-5">
                                <span className="font-bold text-orange font-size-title">
                                £{roundNumber(c.sqm * c.price)}
                                </span>
                                    <span className="text-small">&nbsp;&nbsp; Excl. VAT</span>
                                </div>
                                <div className="col-4 text-end">
                            <span className="font-bold text-orange font-size-title">
                                £{roundNumber(c.price)}
                            </span>
                                    <span className="text-small">&nbsp;&nbsp; per SQM</span>
                                </div>
                            </div>
                        </div>}
                    </div>
                ))}

                {/*<!-- CURRENT CUT -->*/}
                <div className="product-card__accordion mt-3">
                    <div className="product-card__accordion__header">
                        <div className="product-card__accordion__header__title">
                            Cut Length
                        </div>
                        <div onClick={() => setToggleCut(!toggleCut)} className="product-card__accordion__header__toggle">
                            {!toggleCut && <GrDown className="product-card__caret"/>}
                            {toggleCut && <GrUp className="product-card__caret"/>}
                        </div>
                    </div>
                    {toggleCut && <div className="product-card__accordion__body">
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="product-card-ex__price__right">
                                    {hasCutStock() && <div className="green-circle"/>}
                                    {!hasCutStock() && <div className="red-circle"/>}
                                    <div className="product-card-ex__price__right__details">
                                        <p className="text-start">
                                            {hasCutStock() ? 'In Stock' : 'Out Of Stock'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div>Width (m)</div>
                                <div className="mt-3">
                                    {/* @ts-ignore */}
                                    <DropDown items={widths} value={currentWidth}
                                              onSelect={(item) => setCurrentWidth(item.value)}/>
                                </div>
                                <div className="mt-3">
                                    <TextInput disabled bgColor="white" value={`${totalSqm} SQM`}/>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div>Length (m)</div>
                                <div className="mt-3">
                                    <TextInput type="number" bgColor="white"
                                               onChange={(val) => setCurrentLength(lwRoundNumber(val))}
                                               value={currentLength}/>
                                </div>
                                <div className="mt-3">
                                    <Button disabled={!currentLength || !currentWidth || reference.length <= 0 || currentUsedLength() > totalLength} onClick={onAddAnotherCut}
                                            size="stretch" type="grey"
                                            text={`Add ${cuts.length > 0 ? 'Another' : ''} Cut`}/>
                                </div>
                            </div>
                        </div>
                        {!hasCutStock() && <div className="row mt-4">
                            <Button type="link" text="Notify When Back In Stock" onClick={onNotify} />
                        </div>}
                        <div>
                            {/* @ts-ignore */}
                            {currentUsedLength() > totalLength && <div className="mb-3 mt-3 alert alert-danger" role="alert">Max available length: {totalLength}</div>}
                        </div>
                        {hasCutStock() && <div className="row mt-5">
                            <TextInput placeholder="Example: Bedroom" label="Reference" required bgColor="white" value={reference} onChange={setReference}/>
                        </div>}
                        {hasCutStock() && <div className="row mt-5">
                            <div className="col-3">
                                <span className="font-bold font-size-title">Total</span>
                            </div>
                            <div className="col-5">
                            <span className="font-bold text-orange font-size-title">
                            £{roundNumber(totalSqm * (price?.cut_price || 0))}
                            </span>
                                <span className="text-small">&nbsp;&nbsp;Excl. VAT</span>
                            </div>
                            <div className="col-4 text-end">
                            <span className="font-bold text-orange font-size-title">
                                £{roundNumber((price?.cut_price || 0))}
                            </span>
                                <span className="text-small">&nbsp;&nbsp;per SQM</span>
                            </div>
                        </div>}
                        {hasCutStock() && <div className="row mt-5">
                            <Button text="Add All Cuts To Order Summary" disabled={cuts.length <= 0}
                                    onClick={onAddCutsToOrderSummary}/>
                        </div>}
                    </div>}
                </div>

                {/*<!-- ROLLS -->*/}
                <div className="product-card__accordion mt-3">
                    <div className="product-card__accordion__header">
                        <div className="product-card__accordion__header__title">
                            Select a Roll
                        </div>
                        <div onClick={() => setToggleRoll(!toggleRoll)} className="product-card__accordion__header__toggle">
                            {!toggleRoll && <GrDown className="product-card__caret"/>}
                            {toggleRoll && <GrUp className="product-card__caret"/>}
                        </div>
                    </div>
                    {toggleRoll && <div className="product-card__accordion__body">
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="product-card-ex__price__right">
                                    {rollsStock.length > 0 && <div className="green-circle"/>}
                                    {rollsStock.length === 0 && <div className="red-circle"/>}
                                    <div className="product-card-ex__price__right__details">
                                        <p className="text-start">
                                            {rollsStock.length > 0 ? 'In Stock' : 'Out Of stock'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-3">
                            {selectedRoll && <span
                                className="font-bold font-size-title">{selectedRoll.roll_flag === 'F' ? 'Full' : 'Half'} Roll</span>}
                        </div>
                        {selectedRoll && <div className="row mt-3">
                            <div className="col-md-4">
                                <div className="text-orange">Batch ID: <b>{selectedRoll.roll_id}</b></div>
                            </div>
                        </div>}
                        {selectedRoll && <div className="row mt-3">
                            <div className="col-md-4">
                                <div className="font-bold">Width (m)</div>
                                <div>{product?.WIDTH1}</div>
                            </div>
                            <div className="col-md-4">
                                <div className="font-bold">Length (m)</div>
                                <div>{selectedRoll.roll_length}</div>
                            </div>
                            <div className="col-md-4">
                                <div className="font-bold">Price (per SQM)</div>
                                <div>£{roundNumber(price?.roll_price || 0)}</div>
                            </div>
                        </div>}
                        <div>
                            {/* @ts-ignore */}
                            {currentUsedLength() > totalLength && <div className="mb-3 mt-3 alert alert-danger" role="alert">Max available length: {totalLength}</div>}
                        </div>
                        {rollsStock.length > 0 && <div className="row mt-5">
                            <TextInput label="Reference" bgColor="white" required value={rollReference} placeholder="Example: Bedroom" onChange={setRollReference}/>
                        </div>}
                        {rollsStock.length > 0 && <div className="row mt-5">
                            {selectedRoll && <Button disabled={!rollReference || currentUsedLength() > totalLength} text="Add Roll To Order Summary" onClick={onAddRollToOrderSummary}/>}
                        </div>}
                        {rollsStock.length > 0 && <div className="row mt-3">
                            <Button text="View All Available Rolls" type="grey" onClick={() => setToggleRollPopUp(true)}/>
                            <LWModal title="All Available Rolls" onClose={() => setToggleRollPopUp(false)}
                                     show={toggleRollPopUp}>
                                <div className="row">
                                    {product && stock?.roll?.map(r =>
                                      r.roll_id !== '' && <div className="col-md-6" key={r.roll_id}>
                                            <MultiChoice.Rolls
                                                onClick={() => setSelectedRoll(r)}
                                                price={price?.roll_price || 0}
                                                batchId={r.roll_id}
                                                length={r.roll_length}
                                                width={product?.WIDTH1}
                                                selected={r.roll_id === selectedRoll?.roll_id}
                                                rollType={r.roll_flag}
                                            />
                                        </div>)}
                                </div>
                                <div className="row mt-5">
                                    {selectedRoll && <Button text="Select Roll" onClick={onSelectRoll}/>}
                                </div>
                            </LWModal>
                        </div>}
                    </div>}
                </div>

                {/*<!-- ORDER SUMMARY -->*/}
                <div className="product-card__accordion mt-3">
                    <div className="product-card__accordion__header">
                        <div className="product-card__accordion__header__title">
                            Order Summary
                        </div>
                    </div>
                    <div className="product-card__accordion__body">
                        <div className="row">
                            <div className="col-12">
                                <hr/>
                            </div>
                        </div>
                        {orderLines.map((o, i) => <div key={i} className="row mt-2 mb-4">
                            <div className="col-2">
                                <div className="text-small text-grey-tiny">SQM</div>
                                <div className="text-small">{o.length * (o?.width || 0)}</div>
                            </div>
                            <div className="col-4">
                                <div className="text-small text-grey-tiny">Price (per SQM)</div>
                                <div className="text-small">£{roundNumber(o.item_price)}</div>
                            </div>
                            <div className="col-4">
                                <div className="text-small text-grey-tiny">Total (Excl. VAT)</div>
                                <div className="text-small text-start">£{roundNumber(o.line_price)}</div>
                            </div>
                            <div className="col-2">
                                <div className="text-small text-grey-tiny text-center">&nbsp;</div>
                                <div className="text-small d-flex justify-content-between align-items-center">
                                    <div className="cursor-pointer" onClick={() => onMinus(i)}>
                                        <img src={TrashSvg} alt="trash"/>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 text-small text-grey-tiny">{o.product_quality}{o.colour_full_name} - Reference: {o.line_reference || 'N/A'} </div>
                            </div>
                        </div>)}
                        <div className="row">
                            <div className="col-12">
                                <hr/>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-6 font-bold"></div>
                            <div className="col-6 text-end">
                                <span className="font-bold text-small">Total&nbsp;&nbsp;&nbsp;&nbsp;</span>
                                <span className="text-small">Excl. VAT &nbsp;&nbsp;&nbsp;</span>
                                <span className="font-bold text-orange">£{roundNumber(getOrderTotal())}</span>
                            </div>
                        </div>
                        {shallShowUnableColorMatch() && <div className="row mt-5">
                            <div className="alert alert-danger" role="alert">
                                We are unable to guarantee colour match in your selected cuts.
                            </div>
                        </div>}
                        <div className="row mt-4">
                            <Button text="Add Order Summary to Basket and Continue Shopping" disabled={orderLines.length <= 0}
                                    onClick={onAddOrderSummaryToBasketAndContinue}/>
                        </div>
                        <div className="row mt-4">
                            <Button text="Add Order Summary to Basket and Checkout" disabled={orderLines.length <= 0}
                                    onClick={onAddOrderSummaryToBasket}/>
                        </div>
                    </div>
                </div>
            </div>
  );
}
;

export default ProductCard;
